/**
 * ITER179 · SHS AI response audio playback (Phase 7 / Phase 8).
 *
 * Adds a Listen / Pause / Stop control to any assistant response. Uses the
 * existing /api/tts/generate infrastructure (openai onyx voice + server-side
 * cache) so we do not create a second voice system. Playback is user-
 * initiated only — never autoplay. Preserves the visible text response.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Pause, Square, RefreshCw, AlertCircle } from 'lucide-react';

const L = (en, es) => (typeof document !== 'undefined' && document.documentElement.lang === 'es' ? es : en);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Cache one Audio element per (text) so replaying doesn't re-fetch and so
// pause/play remain consistent across the same message content.
const audioBySignature = new Map();

const ListenButton = ({ text, testid = 'ai-listen-button' }) => {
  const [state, setState] = useState('idle'); // idle | loading | playing | paused | error
  const [errorMsg, setErrorMsg] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => () => {
    // Clean up on unmount: stop playback for this component's audio but
    // keep the cached buffer for the next mount (fast replay).
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch { /* noop */ }
    }
  }, []);

  const start = async () => {
    setErrorMsg(null);
    // Resume if paused
    if (state === 'paused' && audioRef.current) {
      try { await audioRef.current.play(); setState('playing'); } catch (e) { setErrorMsg(String(e.message || e)); setState('error'); }
      return;
    }
    // Reuse cached Audio if we've already generated it
    const existing = audioBySignature.get(text);
    if (existing) {
      audioRef.current = existing;
      try { await existing.play(); setState('playing'); } catch (e) { setErrorMsg(String(e.message || e)); setState('error'); }
      return;
    }
    // Generate via server
    setState('loading');
    try {
      const r = await fetch(`${API_BASE}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Audio generation failed (${r.status}).`);
      }
      const data = await r.json();
      // Server returns { audio_url, cached }. audio_url is either a relative
      // path like /api/tts/audio/{key} or an absolute URL. Resolve safely.
      const src = data.audio_url && data.audio_url.startsWith('http')
        ? data.audio_url
        : `${API_BASE}${data.audio_url || ''}`;
      const audio = new Audio(src);
      audio.preload = 'auto';
      audio.addEventListener('ended', () => setState('idle'));
      audio.addEventListener('error', () => { setState('error'); setErrorMsg(L('Playback failed.', 'La reproducción falló.')); });
      audioBySignature.set(text, audio);
      audioRef.current = audio;
      await audio.play();
      setState('playing');
    } catch (e) {
      setState('error');
      setErrorMsg(String(e.message || e));
    }
  };

  const pause = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setState('paused');
    }
  };

  const stop = () => {
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch { /* noop */ }
    }
    setState('idle');
  };

  return (
    <div className="mt-2 flex items-center gap-1.5" data-testid={testid}>
      {state === 'idle' && (
        <button
          type="button"
          onClick={start}
          disabled={!text || text.length < 3}
          aria-label={L('Listen to this response', 'Escuchar esta respuesta')}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-full px-3 min-h-8 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
          data-testid={`${testid}-play`}
        >
          <Volume2 className="h-3 w-3" aria-hidden="true" />
          {L('Listen', 'Escuchar')}
        </button>
      )}
      {state === 'loading' && (
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" role="status" aria-live="polite" data-testid={`${testid}-loading`}>
          <RefreshCw className="h-3 w-3 animate-spin" aria-hidden="true" />
          {L('Loading audio…', 'Cargando audio…')}
        </span>
      )}
      {(state === 'playing' || state === 'paused') && (
        <>
          {state === 'playing' ? (
            <button
              type="button"
              onClick={pause}
              aria-label={L('Pause playback', 'Pausar reproducción')}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-full px-3 min-h-8 hover:bg-slate-50"
              data-testid={`${testid}-pause`}
            >
              <Pause className="h-3 w-3" aria-hidden="true" />
              {L('Pause', 'Pausar')}
            </button>
          ) : (
            <button
              type="button"
              onClick={start}
              aria-label={L('Resume playback', 'Reanudar reproducción')}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-full px-3 min-h-8 hover:bg-slate-50"
              data-testid={`${testid}-resume`}
            >
              <Volume2 className="h-3 w-3" aria-hidden="true" />
              {L('Resume', 'Reanudar')}
            </button>
          )}
          <button
            type="button"
            onClick={stop}
            aria-label={L('Stop playback', 'Detener reproducción')}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 border border-slate-300 rounded-full px-3 min-h-8 hover:bg-slate-50"
            data-testid={`${testid}-stop`}
          >
            <Square className="h-3 w-3" aria-hidden="true" />
            {L('Stop', 'Detener')}
          </button>
        </>
      )}
      {state === 'error' && (
        <span className="inline-flex items-center gap-1 text-[11px] text-rose-700" role="alert" data-testid={`${testid}-error`}>
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          {errorMsg || L('Playback failed.', 'La reproducción falló.')}
          <button type="button" onClick={start} className="underline underline-offset-2 ml-1" data-testid={`${testid}-retry`}>{L('Retry', 'Reintentar')}</button>
        </span>
      )}
    </div>
  );
};

export default ListenButton;
